// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import SearchFieldWrapper from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Select from "@insite/mobius/Select";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DatePicker, {
    DatePickerPresentationProps,
    DatePickerState,
} from "@store/MobiusOverrides/DatePicker/DatePicker";
import constants from "@store/constants";
import hasSameParams from "@store/Framework/Common/Utilities/Events/hasSameParams";
import getCurrentDateMinuteZero from "@store/Framework/Common/Utilities/getCurrentDateMinuteZero";
import substractDays from "@store/Framework/Common/Utilities/substractDays";
import { getEventParameters } from "@store/Framework/Store/Store/Pages/EventList/EventListSelector";
import { GetEventsApiParameter } from "@store/Framework/Store/Store/Pages/EventList/Handlers/GetEventList";
import updateLoadParameter from "@store/Framework/Store/Store/Pages/EventList/Handlers/UpdateLoadParameter";
import { buttonStyles } from "@store/Themes/common/mixins/button";
import { linkStyles } from "@store/Themes/common/mixins/link";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    startDate = "startDate",
    endDate = "endDate",
    category = "category",
    province = "province",
    pageSize = "pageSize",
    isFeatured = "isFeatured",
}

export interface EventListFiltersStyles {
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    dateWrapperGridItem?: GridItemProps;
    dropdownGridItem?: GridItemProps;
    datePickersContainer?: GridContainerProps;
    dateGridItem?: GridItemProps;
    datePickerStyle?: DatePickerPresentationProps;
    btnsWrapper?: InjectableCss;
    searchButton?: ButtonPresentationProps;
    resetButton?: ClickablePresentationProps;
}

const eventListFiltersStyles: EventListFiltersStyles = {
    wrapper: {
        css: css`
            display: flex;

            ${breakpointValues(["wrap", null, null, "nowrap"], ["flex-wrap"])}
        `,
    },
    gridContainer: {
        css: css`
            ${GridOffset} {
                ${breakpointValues(["wrap", null, null, "nowrap"], ["flex-wrap"])}
            }
        `,
    },
    gridItem: {
        width: [12, 12, 6, 6, 6],
    },
    dateWrapperGridItem: {
        width: [12, 12, 12, 5, 4],
        css: css`
            position: relative;

            ${breakpointValues([null, null, null, `${pxToRem(30)} !important`], ["margin-right"])}

            &::after {
                content: "";
                width: ${pxToRem(2)};
                height: calc(100% - ${pxToRem(30)});
                background-color: ${getColor("common.border")};
                position: absolute;
                right: -${pxToRem(15)};
                top: 50%;
                transform: translateY(-50%);

                ${breakpointValues(["none", null, null, "block"], ["display"])}
            }
        `,
    },
    dropdownGridItem: {
        width: [12, 12, 12, 7, 8],
    },
    dateGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    btnsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            align-self: flex-start;
            margin-top: ${pxToRem(30)};

            ${breakpointValues(["100%", null, null, "auto"], ["width"])}
            ${breakpointValues(["auto", null, null, pxToRem(45)], ["height"])}
            ${breakpointValues(["center", null, null, "flex-end"], ["justify-content"])}
            ${breakpointValues(["wrap", null, null, "nowrap"], ["flex-wrap"])}
            ${breakpointValues(["0", null, null, `${pxToRem(30)}`], ["margin-left"])}
        `,
    },
    searchButton: {
        variant: "primary",
        css: css`
            ${buttonStyles("solid")}
            ${breakpointValues(["100%", null, "auto"], ["width"])}
        `,
    },
    resetButton: {
        css: css`
            ${linkStyles("primary", "primary.dark", true, false)}

            ${breakpointValues(
                [`${pxToRem(15)} 0 0 0`, null, `0 0 0 ${pxToRem(30)}`, `0 0 0 ${pxToRem(15)}`],
                ["margin"],
            )}
        `,
    },
};

const styles = eventListFiltersStyles;

const EventListFilters: React.FunctionComponent<HasHistory> = ({ history }) => {
    const getEventsApiParameter = useAppSelector(state => getEventParameters(state));
    const location = useAppSelector(state => getLocation(state));
    const [initBasedOnParams, setInitBasedOnParams] = useState(false);

    const minFromDate = getCurrentDateMinuteZero();
    const initialParameter: GetEventsApiParameter = { startDate: convertDateToDateOnlyString(minFromDate) };
    const dispatch = useDispatch();

    const [expandAccordion, setExpandAccordion] = useState(false);
    const [fromDate, setFromDate] = useState<Date | undefined>(minFromDate);
    const [toDate, setToDate] = useState<Date | undefined>();
    const [isInvalidToDate, setIsInvalidToDate] = useState(false);
    const [apiParameter, setApiParameter] = useState<GetEventsApiParameter>({ ...initialParameter });
    const [searchParameter, setSearchParameter] = useState<GetEventsApiParameter | undefined>();
    const [resetFilters, setResetFilters] = useState<boolean>(false);
    const { isLoading, value: eventList } = useAppSelector(state => getDataView(state.data.pages, initialParameter));

    const [categoryList, setCategoryList] = useState<string[] | undefined>();
    const [provinceSelector, setProvinceSelector] = useState<string[] | undefined>();

    useEffect(() => {
        if (eventList) {
            const _categories = eventList
                ?.map((event: PageProps) => event.fields.categoriesList as string[])
                ?.filter((item: string[]) => item !== undefined && item.length);
            const _categoryList = getFilterItems(_categories);
            setCategoryList(_categoryList);

            const _provinces = eventList
                ?.map((event: PageProps) => event.fields.provinceSelector as string[])
                ?.filter((item: string[]) => item !== undefined && item.length);
            const _provinceSelector = getFilterItems(_provinces);
            setProvinceSelector(_provinceSelector);
        }
    }, [eventList]);

    useEffect(() => {
        if (!initBasedOnParams && location) {
            const parsed = parseQueryString<GetEventsApiParameter | undefined>(location.search);
            if (parsed) {
                delete parsed.startDate;
                delete parsed.endDate;
            }
            const apiParameter = { ...getEventsApiParameter, ...parsed };

            if (
                (parsed?.category && categoryList) ||
                (parsed?.province && provinceSelector) ||
                (!parsed?.category && !parsed?.province)
            ) {
                if (parsed?.category && !categoryList!.includes(parsed?.category)) {
                    delete apiParameter.category;
                }
                if (parsed?.province && !provinceSelector!.includes(parsed?.province)) {
                    delete apiParameter.province;
                }
                setInitBasedOnParams(true);
                setApiParameter(apiParameter);
                setSearchParameter(apiParameter);
            }
        }
    }, [location, categoryList, provinceSelector, initBasedOnParams]);

    useEffect(() => {
        if (initBasedOnParams) {
            const eventsApiParameter = { ...getEventsApiParameter };
            delete eventsApiParameter.startDate;
            delete eventsApiParameter.endDate;
            const queryString = qs.stringify(eventsApiParameter);
            if (location.search !== `?${queryString}`) {
                history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
            }
        }
    }, [getEventsApiParameter]);

    useEffect(() => {
        if (searchParameter !== undefined) {
            dispatch(updateLoadParameter(searchParameter));
        }
    }, [searchParameter]);

    useEffect(() => {
        if (resetFilters) {
            setSearchParameter(apiParameter);
            setResetFilters(false);
        }
    }, [resetFilters]);

    const getFilterItems = (itemList: string[][] | undefined) => {
        const filteredItems: string[] = [];

        itemList?.map((item: string[]) =>
            /* eslint-disable-next-line array-callback-return */
            item.map((i: string) => {
                if (!filteredItems.includes(i)) {
                    filteredItems.push(i);
                }
            }),
        );

        return filteredItems;
    };

    const fromDateChangeHandler = ({ selectedDay }: DatePickerState) => {
        const value = convertDateToDateOnlyString(selectedDay);
        const parameters = { ...apiParameter, startDate: value };
        setFromDate(selectedDay);
        cleanAndSetApiParameter(parameters);
    };

    const toDateChangeHandler = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        const value = convertDateToDateOnlyString(selectedDay);
        const parameters = { ...apiParameter, endDate: value };
        setToDate(selectedDay);
        setIsInvalidToDate(selectedDayDisabled);
        cleanAndSetApiParameter(parameters);
    };

    const categoryChangeHandler = (value: string) => {
        const parameters = { ...apiParameter, category: value };
        cleanAndSetApiParameter(parameters);
    };

    const provinceChangeHandler = (value: string) => {
        const parameters = { ...apiParameter, province: value };
        cleanAndSetApiParameter(parameters);
    };

    const cleanAndSetApiParameter = (_parameters: any) => {
        const parameters = Object.keys(_parameters).reduce((acc: any, key) => {
            const value = _parameters[key];
            if (value != null && value !== "") {
                acc[key] = value;
            }
            return acc;
        }, {});
        setApiParameter(parameters);
    };

    const searchFiltersHandler = () => {
        if (searchParameter && !hasSameParams(apiParameter, searchParameter)) {
            setSearchParameter(apiParameter);
        }
    };

    /* Temporarily removed as it does not work with the current code to reset an invalid EndDate. */
    /* const resetFiltersHandler = () => {
        if (!hasSameParams(apiParameter, initialParameter)) {
            setApiParameter({ ...initialParameter });
            setResetFilters(true);
            setFromDate(minFromDate);
            setToDate(undefined);
        }
    }; */

    const renderFilters = () => (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.dateWrapperGridItem}>
                    <GridContainer {...styles.datePickersContainer}>
                        <GridItem {...styles.dateGridItem}>
                            <DatePicker
                                {...styles.datePickerStyle}
                                label={translate("Events From")}
                                selectedDay={fromDate}
                                onDayChange={fromDateChangeHandler}
                                dateTimePickerProps={{
                                    minDate: minFromDate,
                                    maxDate: toDate ? substractDays(toDate, 1) : undefined,
                                }}
                            />
                        </GridItem>
                        <GridItem {...styles.dateGridItem}>
                            <DatePicker
                                {...styles.datePickerStyle}
                                label={translate("To")}
                                selectedDay={toDate}
                                onDayChange={toDateChangeHandler}
                                dateTimePickerProps={{
                                    minDate: addDays(fromDate || new Date(), 1),
                                }}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.dropdownGridItem}>
                    <GridContainer {...styles.gridContainer}>
                        <GridItem {...styles.gridItem}>
                            <Select
                                label={translate("Categories")}
                                onChange={e => categoryChangeHandler(e.currentTarget.value)}
                                id="selectCategory"
                                value={apiParameter.category || ""}
                            >
                                <option value="">{translate("Categories")}</option>
                                {categoryList?.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.gridItem}>
                            <Select
                                label={translate("Province")}
                                onChange={e => provinceChangeHandler(e.currentTarget.value)}
                                id="selectProvince"
                                value={apiParameter.province || ""}
                            >
                                <option value="">{translate("Province")}</option>
                                {provinceSelector?.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.btnsWrapper}>
                <Button
                    {...styles.searchButton}
                    disabled={isLoading || !apiParameter.startDate || isInvalidToDate}
                    onClick={searchFiltersHandler}
                >
                    {translate("Search")}
                </Button>
                {/* <Clickable {...styles.resetButton} disabled={isLoading} onClick={resetFiltersHandler}>
                    {translate("Reset")}
                </Clickable> */}
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <SearchFieldWrapper>
            <Hidden above="md">
                <Accordion headingLevel={6}>
                    <AccordionSection
                        title={translate("Filters")}
                        expanded={expandAccordion}
                        onTogglePanel={() => setExpandAccordion(!expandAccordion)}
                    >
                        {renderFilters()}
                    </AccordionSection>
                </Accordion>
            </Hidden>
            <Hidden below="lg">{renderFilters()}</Hidden>
        </SearchFieldWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(EventListFilters),
    definition: {
        group: constants.widgetGroup.eventList,
        displayName: "Event List Filters",
        allowedContexts: ["EventListPage"] as any,
    },
};

export default widgetModule;
